import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';

/**
 * MetricCard - A metric display card for the unified financial wellness dashboard
 *
 * Used in the hero section to display key financial metrics like:
 * - Student Loan balance
 * - Employer help YTD/lifetime
 * - Tax savings
 * - PSLF progress or Career score
 *
 * @example
 * ```gts
 * <MetricCard
 *   @icon={{PhBank}}
 *   @label="Student Loans"
 *   @value="$42,000"
 *   @subtext="5.2 years to payoff"
 *   @valueClass="text-white"
 * >
 *   <:trend>
 *     <PhTrendDown @size={{14}} class="text-emerald-300" />
 *   </:trend>
 * </MetricCard>
 * ```
 */
interface MetricCardSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Label text displayed above the value */
    label: string;
    /** Main value to display (formatted string, e.g. "$42,000") */
    value: string;
    /** Secondary text below the value */
    subtext?: string;
    /** Optional CSS class for value styling (e.g. for colored values) */
    valueClass?: string;
  };
  Blocks: {
    /** Optional trend indicator (icon + text) */
    trend?: [];
  };
  Element: HTMLDivElement;
}

const MetricCard: TOC<MetricCardSignature> = <template>
  <div
    class="bg-white/10 rounded-xl p-4 backdrop-blur-sm"
    role="group"
    aria-label={{@label}}
    ...attributes
  >
    <div class="flex items-center gap-2 text-white/90 mb-1">
      {{#let @icon as |Icon|}}
        <Icon @size={{16}} />
      {{/let}}
      <span class="text-xs uppercase tracking-wide">{{@label}}</span>
    </div>
    <div class="text-2xl md:text-3xl font-bold {{if @valueClass @valueClass 'text-white'}}">
      {{@value}}
    </div>
    {{#if @subtext}}
      <div class="text-sm text-white/70 flex items-center gap-1 mt-1">
        {{#if (has-block "trend")}}
          {{yield to="trend"}}
        {{/if}}
        <span>{{@subtext}}</span>
      </div>
    {{/if}}
  </div>
</template>;

export default MetricCard;
